'use client'

import { TokenDetailsFragment } from '@/gql/generated/graphql'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/accessible-accordion'
import { MarkdownDisplay } from '@components/markdown-text'
import { BodyText, DetailText, HeadlineText } from 'design-configuration/components'
import { ComponentProps } from 'react'
import { formatDate } from '../utils'
import { getEtherscanAddressURL } from '@utils/routes'

export function Details({ token }: { token: TokenDetailsFragment }) {
  const project = token.project
  const artistBio = project.artistPage?.data?.[0]?.attributes?.bio

  return (
    <div className="p-sm">
      {project.tags.length > 0 ? (
        <DetailsItem label="Tags" value={project.tags.map((tag) => tag.tag?.display_name).join(', ')} />
      ) : null}
      <DetailsItem label="Library" value={project.dependency_name_and_version ?? 'unknown'} />
      <DetailsItem label="Aspect Ratio" value={project.aspect_ratio} />
      <DetailsItem
        label="Collection Size"
        value={`${project.max_invocations ?? '1000000'} unique artworks${
          project.max_invocations - (project.invocations ?? 0) !== 0
            ? `, ${project.max_invocations - (project.invocations ?? 0)} remaining`
            : ''
        }`}
      />
      <DetailsItem label="Token ID" value={token.token_id} />
      <DetailsItem
        label="Contract Address"
        value={
          <a href={getEtherscanAddressURL(token.contract_address)} target="_blank" className="underline">
            {`${token.contract_address.slice(0, 4)}...${token.contract_address.slice(-4)}`}
          </a>
        }
      />
      <DetailsItem label="Contract Type" value={token.project.vertical_name === 'flex' ? 'Flex' : 'Fully on-chain'} />
      <DetailsItem label="Token Standard" value="ERC-721" />
      <DetailsItem label="Mint Date" value={formatDate(token.minted_at)} />
      <Accordion type="single" collapsible>
        {project.artist_display_notes ? (
          <DetailsAccordionIem value="artist-display-notes">
            <DetailsAccordionTrigger>Display notes</DetailsAccordionTrigger>
            <AccordionContent>
              <BodyText>{project.artist_display_notes}</BodyText>
            </AccordionContent>
          </DetailsAccordionIem>
        ) : null}
        {project.sales_notes ? (
          <DetailsAccordionIem value="sales-notes">
            <DetailsAccordionTrigger>Sales notes</DetailsAccordionTrigger>
            <AccordionContent>
              <BodyText>{project.sales_notes}</BodyText>
            </AccordionContent>
          </DetailsAccordionIem>
        ) : null}
        {project.description ? (
          <DetailsAccordionIem value="description">
            <DetailsAccordionTrigger>About the collection</DetailsAccordionTrigger>
            <AccordionContent>
              <MarkdownDisplay
                className="mb-xs"
                componentOverrides={{
                  h1: ({ children }) => <HeadlineText>{children}</HeadlineText>,
                  h2: ({ children }) => <HeadlineText>{children}</HeadlineText>,
                  h3: ({ children }) => <HeadlineText>{children}</HeadlineText>
                }}
              >
                {project.description}
              </MarkdownDisplay>
            </AccordionContent>
          </DetailsAccordionIem>
        ) : null}
        {artistBio ? (
          <DetailsAccordionIem value="artist-bio">
            <DetailsAccordionTrigger>About the artist</DetailsAccordionTrigger>
            <AccordionContent>
              <MarkdownDisplay
                componentOverrides={{
                  h1: ({ children }) => <HeadlineText>{children}</HeadlineText>,
                  h2: ({ children }) => <HeadlineText>{children}</HeadlineText>,
                  h3: ({ children }) => <HeadlineText>{children}</HeadlineText>
                }}
              >
                {artistBio}
              </MarkdownDisplay>
            </AccordionContent>
          </DetailsAccordionIem>
        ) : null}
      </Accordion>
    </div>
  )
}

function DetailsAccordionIem(props: ComponentProps<typeof AccordionItem>) {
  return <AccordionItem {...props} className="first-of-type:border-t-0" />
}

function DetailsAccordionTrigger(props: ComponentProps<typeof AccordionTrigger>) {
  return <AccordionTrigger {...props} className="[&>svg]:fill-on-surface-variant" />
}

function DetailsItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-xs border-outline-variant flex items-center justify-between border-b">
      <DetailText className="text-on-surface font-medium uppercase">{label}</DetailText>
      <BodyText className="text-on-surface-variant text-right">{value}</BodyText>
    </div>
  )
}
